import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Upload } from "lucide-react";
import { fileToBase64 } from "@/lib/adminApi";
import { toast } from "sonner";

export const inputCls =
  "rounded-none border-x-0 border-t-0 border-b-2 border-[#E5DFD3] focus-visible:ring-0 focus-visible:border-[#0A2342] px-0";
export const textareaCls =
  "rounded-none border-2 border-[#E5DFD3] focus-visible:ring-0 focus-visible:border-[#0A2342]";

export const FieldLabel = ({ children }) => (
  <Label className="text-[10px] uppercase tracking-[0.22em] text-[#5C636A] font-semibold">
    {children}
  </Label>
);

export const Field = ({ label, children, full = false }) => (
  <div className={`space-y-2 ${full ? "sm:col-span-2" : ""}`}>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </div>
);

export const TextField = ({ label, value, onChange, placeholder, full = false, type = "text" }) => (
  <Field label={label} full={full}>
    <Input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputCls}
    />
  </Field>
);

export const TextAreaField = ({ label, value, onChange, rows = 3, full = true }) => (
  <Field label={label} full={full}>
    <Textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className={textareaCls}
    />
  </Field>
);

export const ImageField = ({ label, value, onChange, testId, helpText, previewClass = "h-14 w-auto" }) => {
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Image must be under 1 MB. Compress first.");
      return;
    }
    const b64 = await fileToBase64(file);
    onChange(b64);
    toast.message("Image loaded. Click Save to apply.");
  };
  return (
    <div className="sm:col-span-2">
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-3 flex items-center gap-5 flex-wrap">
        {value ? (
          <img src={value} alt="preview" className={`${previewClass} object-cover border border-[#E5DFD3] bg-[#FDFBF7]`} />
        ) : (
          <div className={`${previewClass} border border-dashed border-[#E5DFD3] flex items-center justify-center text-xs text-[#5C636A] px-3`}>
            {helpText || "No image"}
          </div>
        )}
        <label className="inline-flex items-center gap-2 cursor-pointer border border-[#0A2342] px-4 py-2 text-xs uppercase tracking-wider text-[#0A2342] hover:bg-[#0A2342] hover:text-white transition-colors">
          <Upload className="w-3.5 h-3.5" /> Upload
          <input data-testid={testId} type="file" accept="image/*" className="hidden" onChange={handle} />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs uppercase tracking-wider text-[#5C636A] hover:text-[#0A2342]"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

export const StringListEditor = ({ label, items, onChange, placeholder }) => {
  const update = (i, v) => {
    const list = [...(items || [])];
    list[i] = v;
    onChange(list);
  };
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => {
    const list = [...(items || [])];
    list.splice(i, 1);
    onChange(list);
  };
  return (
    <div className="sm:col-span-2">
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-3 space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input
              value={it}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              className={inputCls}
            />
            <button type="button" onClick={() => remove(i)} className="p-2 text-[#5C636A] hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#0A2342] border border-[#0A2342] px-3 py-2 hover:bg-[#0A2342] hover:text-white"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
};

export const ParagraphListEditor = ({ label, items, onChange, rows = 3 }) => {
  const update = (i, v) => {
    const list = [...(items || [])];
    list[i] = v;
    onChange(list);
  };
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => {
    const list = [...(items || [])];
    list.splice(i, 1);
    onChange(list);
  };
  return (
    <div className="sm:col-span-2">
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-3 space-y-3">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2 items-start">
            <Textarea value={it} onChange={(e) => update(i, e.target.value)} rows={rows} className={textareaCls} />
            <button type="button" onClick={() => remove(i)} className="p-2 text-[#5C636A] hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#0A2342] border border-[#0A2342] px-3 py-2 hover:bg-[#0A2342] hover:text-white"
        >
          <Plus className="w-3.5 h-3.5" /> Add paragraph
        </button>
      </div>
    </div>
  );
};

// Generic CRUD list editor for arrays of {title, body} or similar
export const ObjectListEditor = ({ label, items, onChange, fields, newItem, addLabel }) => {
  const updateItem = (i, key, value) => {
    const list = [...(items || [])];
    list[i] = { ...list[i], [key]: value };
    onChange(list);
  };
  const add = () => onChange([...(items || []), { ...newItem }]);
  const remove = (i) => {
    const list = [...(items || [])];
    list.splice(i, 1);
    onChange(list);
  };

  return (
    <div className="sm:col-span-2">
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-3 space-y-4">
        {(items || []).map((it, i) => (
          <div key={i} className="border border-[#E5DFD3] p-4 bg-[#FDFBF7]">
            <div className="flex justify-between items-center mb-3">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C636A]">
                #{i + 1}
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-[#5C636A] hover:text-red-600 inline-flex items-center gap-1 text-xs uppercase"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <Field key={f.key} label={f.label} full={f.full}>
                  {f.textarea ? (
                    <Textarea
                      rows={f.rows || 3}
                      value={it[f.key] ?? ""}
                      onChange={(e) => updateItem(i, f.key, e.target.value)}
                      className={textareaCls}
                    />
                  ) : (
                    <Input
                      value={it[f.key] ?? ""}
                      onChange={(e) => updateItem(i, f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className={inputCls}
                    />
                  )}
                </Field>
              ))}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#0A2342] border border-[#0A2342] px-4 py-3 hover:bg-[#0A2342] hover:text-white"
        >
          <Plus className="w-3.5 h-3.5" /> {addLabel || "Add"}
        </button>
      </div>
    </div>
  );
};

export const SaveBar = ({ onSave, loading, testId }) => (
  <div className="mt-8 pt-6 border-t border-[#E5DFD3] flex items-center justify-end gap-3">
    <span className="text-xs text-[#5C636A]">Changes go live immediately after saving.</span>
    <button
      type="button"
      data-testid={testId}
      onClick={onSave}
      disabled={loading}
      className="bg-[#0A2342] text-white px-6 py-3 text-xs uppercase tracking-wider hover:bg-[#173B63] disabled:opacity-60"
    >
      {loading ? "Saving..." : "Save changes"}
    </button>
  </div>
);
